/**
 * Hazard stripe shown on non-production environments so facilitators
 * never confuse a DEV or STAGING instance with live workshop traffic.
 */
import { useLocation } from 'react-router-dom';
import styled from '@emotion/styled';

// Thin enough to sit inside a header's top padding: it marks the edge of the
// page instead of covering the title and controls under it.
const Ribbon = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 99999;
  height: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  background: repeating-linear-gradient(
    -45deg,
    #f5d90a,
    #f5d90a 6px,
    #111 6px,
    #111 12px
  );
  color: #111;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.2em;

  span {
    background: #f5d90a;
    padding: 0 6px 0 8px;
    line-height: 10px;
  }
`;

// The ribbon's label, or null on production.
function getEnvironmentLabel() {
  // An environment may switch the stripe off for a workshop run on it (TKI on dev,
  // 29.10.2026): VITE_ENV_RIBBON=off in that Vercel project, removed when the day is over.
  if (import.meta.env.VITE_ENV_RIBBON === 'off') return null;
  if (import.meta.env.DEV) return 'DEV';
  try {
    const socketUrl = import.meta.env.VITE_SOCKET_URL || '';
    if (socketUrl) {
      const host = new URL(socketUrl, window.location.origin).hostname;
      if (host.includes('-staging')) return 'STAGING';
      if (host.includes('-dev')) return 'DEV';
    }
  } catch {
    // ignore malformed URL
  }
  if (typeof window !== 'undefined') {
    if (window.location.hostname === 'kuutio-staging.vercel.app') return 'STAGING';
    if (window.location.hostname === 'kuutio-dev.vercel.app') return 'DEV';
  }
  return null;
}

export default function EnvironmentRibbon() {
  const location = useLocation();
  const isReportPath =
    location.pathname === '/raportti' ||
    location.pathname.startsWith('/raportti/') ||
    location.pathname === '/report' ||
    location.pathname.startsWith('/report/');

  const label = getEnvironmentLabel();
  if (!label || isReportPath) return null;

  return (
    <Ribbon aria-hidden="true">
      <span>{label}</span>
    </Ribbon>
  );
}
